import { createFileRoute, redirect } from '@tanstack/react-router';

import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { usePageMeta } from '@/app/shell/use-page-meta';
import { canAdministerUsers, redirectPathForRole } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import { CentralUsersList } from '@/features/users';

/*
 * TASK-6.2 STUB — NOT THE IMPLEMENTATION.
 *
 * The central user-administration page, SITED WITH SYSTEM CONFIGURATION under
 * `/admin/users` (F02 R17, AC-3388e24d) — the admin area, not with billing
 * operations. Task 6.2 builds the guard and the page to satisfy the failing
 * task-6.1 tests in admin.users.guard.test.ts:
 *
 *   - AC-99623425 — a `beforeLoad` guard reads the derived session from the
 *     auth store IMPERATIVELY (guards run outside React, per AGENTS.md) and,
 *     when the person does NOT hold the is_administrator privilege
 *     (`canAdministerUsers` is false), throws a redirect that RETURNS THEM TO
 *     THEIR OWN HOME PAGE (`redirectPathForRole`). The stub's guard is a no-op,
 *     so a non-privileged person is NOT yet redirected — the test fails for
 *     missing implementation.
 *   - AC-58d352fb — the way IN to user administration is withheld from a
 *     non-privileged person: the nav entry pointing here is gated on
 *     `canAdministerUsers`, and the guard above is the backstop so a hand-typed
 *     URL still bounces home. (The nav placement itself is task 6.2.)
 *
 * Placement is asserted structurally (the route id is `/admin/...`, under the
 * admin/system-configuration area, not `/invoices` or another billing path).
 */
export const Route = createFileRoute('/_auth/admin/users')({
  /*
   * The is_administrator privilege guard (F02 R17, AC-99623425 / AC-58d352fb).
   * Guards run OUTSIDE React, so the derived session is read from the auth store
   * IMPERATIVELY (getState(), never a hook — AGENTS.md). A person who does not
   * hold the privilege is RETURNED TO THEIR OWN HOME PAGE via the central
   * redirect map (`redirectPathForRole`, '/'); the gate is the PRIVILEGE, not a
   * role, so an admin-ROLE account without the switch is bounced too. This is
   * the backstop that makes a hand-typed /admin/users unreachable even though
   * the nav way-in is also hidden. The server (task 1) is the authority and
   * refuses the underlying requests regardless — this only withholds the page.
   */
  beforeLoad: () => {
    const session = useAuthStore.getState().session;
    if (!canAdministerUsers(session)) {
      throw redirect({
        to: redirectPathForRole(session?.role ?? 'client'),
      });
    }
  },
  component: AdminUsersPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function AdminUsersPage() {
  usePageMeta({ title: 'User administration' });
  /*
   * The central, scope-bounded user list. The entity-record Users section
   * (features/users EntityUsersSection) is the per-organization surface
   * (AC-5f7370f6); the central page's cross-organization list over the
   * session's scope is wired as the slice's entity-selection seam lands. Kept
   * minimal-but-real here: the guard above (AC-99623425) is what this task
   * builds, and the placement is asserted structurally.
   */
  return (
    <Page>
      <PageHeader
        title="User administration"
        description="Manage user accounts within your scope."
      />
      {/* The central, scope-bounded user list (F02 R37). Shows each account's
          role, status and the permission set it holds — the set without opening
          the account (AC-6a84fe2f, AC-c2e4df83). The server (GET /users) scopes
          the list to the operator's own organization (AC-d621f792); the list
          renders exactly those rows. */}
      <CentralUsersList />
    </Page>
  );
}
